import React from "react";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";
import {
	FiArrowLeft,
	FiExternalLink,
	FiEdit3,
	FiMail,
	FiPhone,
} from "react-icons/fi";
import { FaMagic } from "react-icons/fa";
import { Nav, Tab } from "react-bootstrap";
import Layout from "components/Layout";
import Loading from "components/Loading";
import EmployeeInfo from "components/Employees/EmployeeInfo";
import EmployeeCustomFields from "components/Employees/EmployeeCustomFields";
import EmployeeGroups from "components/Employees/EmployeeGroups";
import EmployeeFunctions from "components/Employees/EmployeeFunctions";
import EmployeeContracts from "components/Employees/EmployeeContracts";
import EmployeeDocuments from "components/Employees/EmployeeDocuments";
import EmployeeUbications from "components/Employees/EmployeeUbications";
import EmployeeShiftPatterns from "components/Employees/EmployeeShiftPatterns";
import EmployeeMagicPlan from "components/Employees/EmployeeMagicPlan";
import EmployeePayroll from "components/Employees/EmployeePayroll";
import EmployeePendingConfigurationWarnings from "components/Employees/EmployeePendingConfigurationWarnings";
import AbsenceHeader from "components/Absences/AbsenceHeader";
import Avatar from "components/Avatar";
import AvatarEdit from "components/Avatar/AvatarEdit";
import EmployeePlanOpenerModal from "components/Employees/EmployeePlanOpenerModal";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import { employeesService, dashboardsService, timesService } from "services";
import { showModal, hideModal } from "actions/modalActions";
import * as employeesActions from "actions/employeesActions";
import * as groupsActions from "actions/groupsActions";
import * as shiftsActions from "actions/shiftsActions";
import * as functionsActions from "actions/functionsActions";
import * as ubicationsActions from "actions/ubicationsActions";
import * as rulesActions from "actions/rulesActions";
import * as customFieldsActions from "actions/customFieldsActions";
import * as dashboardActions from "actions/dashboardActions";
import { getWorkedTimeCounter } from "utils/datetime";
import moment from "moment";
import EmployeeTimer from "components/Timer/EmployeeTimer";
import Modal from "components/Modal";
import UpgradeModal from "components/Modal/UpgradeModal";
import { isAdmin, isSupervisor, hasSubscriptionFeature } from "utils/utils";
import { withTranslation } from "react-i18next";

class Employee extends React.Component {
	constructor(props) {
		super(props);
		this.closeModal = this.closeModal.bind(this);
		this.openAlertModal = this.openAlertModal.bind(this);

		this.state = {
			loading: true,
			isMe: false,
			employee: {},
			errors: {},
			error_footer: "",
			vacationData: null,
			hourData: null,
			timeRegistrations: [],
			ongoingTime: null,
			todayTotalTime: null,
			firstEntryTime: null,
			hasTimeRegistrations: false,
			// Control de carga de tabs
			loadedTabs: new Set(),
			activeTab: "info",
			documentosRefreshKey: 0,
			// Modal de upgrade
			showUpgradeModal: false,
			upgradeFeature: "",
			configurationWarningsRefreshKey: 0,
			assignmentModalTab: null,
		};
	}

	closeModal() {
		this.props.hideModal();
	}

	openAlertModal(message, title) {
		this.props.showModal(
			{
				open: true,
				title: title || "Error",
				style: { width: "400px" },
				message: message,
				closeModal: this.closeModal,
			},
			"alert"
		);
	}

	// Obtener el grupo activo del empleado (sin fecha de fin o con la fecha más reciente)
	getActiveGroup() {
		const { employee_groups } = this.props;
		if (!employee_groups || employee_groups.length === 0) return null;

		// Buscar grupos activos (sin fecha de fin o fecha de fin en el futuro)
		const today = moment();
		const activeGroups = employee_groups.filter(
			(eg) => !eg.end_day || moment(eg.end_day).isAfter(today)
		);

		if (activeGroups.length === 0) return null;

		// Devolver el grupo con la fecha de inicio más reciente
		const sortedGroups = activeGroups.sort((a, b) =>
			moment(b.start_day).diff(moment(a.start_day))
		);

		return sortedGroups[0].group.id;
	}

	// Abrir modal de planificación
	openPlanModal = () => {
		const activeGroupId = this.getActiveGroup();

		// Período predeterminado: semana actual (lunes a domingo)
		const start = moment().startOf("isoWeek").format("YYYY-MM-DD"); // Lunes (ISO week)
		const end = moment().endOf("isoWeek").format("YYYY-MM-DD"); // Domingo (ISO week)

		this.props.showModal(
			{
				open: true,
				title: this.props.t("employee.newPlan"),
				style: { width: "460px" },
				content: (
					<EmployeePlanOpenerModal
						group_id={activeGroupId || ""}
						start={start}
						end={end}
						handleClose={this.closeModal}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	openAvatarModal = () => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("employee.editAvatar"),
				style: { width: "300px" },
				content: (
					<AvatarEdit
						userId={this.state.employee.id}
						onClose={() => this.props.hideModal()}
					/>
				),
				closeModal: this.closeModal,
			},
			"empty"
		);
	};

	async componentDidMount() {
		await this.loadEmployeeData();
	}

	async componentDidUpdate(prevProps) {
		// Si cambia el ID del empleado en la URL, recargar los datos
		if (prevProps.match.params.id !== this.props.match.params.id) {
			await this.loadEmployeeData();
		}

		// Si el empleado se actualiza en el reducer y es el mismo empleado que estamos viendo, sincronizar el estado local
		if (
			prevProps.employeeFromStore !== this.props.employeeFromStore &&
			this.props.employeeFromStore &&
			this.props.employeeFromStore.id &&
			this.props.employeeFromStore.id === this.state.employee.id
		) {
			this.setState({
				employee: this.props.employeeFromStore,
			});
		}
	}

	componentWillUnmount() {
		// Limpiar el estado global del reducer al salir de la ficha.
		this.props.clearEmployeeState();
	}

	loadEmployeeData = async () => {
		this.setState({ loading: true });

		const employee = await employeesService.getById(this.props.match.params.id);

		if (employee.ok) {
			// Actualizar el estado primero
			this.setState({
				employee,
				loading: false,
				isMe: employee.id === this.props.user.id,
				// Resetear el estado de las tabs cuando cambia el empleado
				loadedTabs: new Set(),
				activeTab: "info",
				documentosRefreshKey: 0,
				assignmentModalTab: null,
			});

			// Actualizar el estado global del reducer con el nuevo empleado
			this.props.updateEmployeeInState(employee);

			// Cargar solo los datos necesarios para el sidebar
			// Pasar employee directamente para evitar problemas de timing con setState asíncrono
			await this.loadSidebarData(employee);
		} else {
			this.setState({
				loading: false,
			});
		}
	};

	loadVacationData = async (employee = null) => {
		try {
			// Usar el employee pasado como parámetro o el del estado
			const employeeToUse = employee || this.state.employee;

			// Validación defensiva: verificar que employee y employee.id existan
			if (!employeeToUse || !employeeToUse.id) {
				console.warn(
					"No se puede cargar vacation data: employee o employee.id es undefined",
					{
						employeeParam: employee,
						stateEmployee: this.state.employee,
					}
				);
				return;
			}

			const currentYear = new Date().getFullYear();
			const response = await dashboardsService.getEmployeeVacationSummary(
				employeeToUse.id,
				currentYear
			);

			if (response.ok) {
				this.setState({ vacationData: response });
			}
		} catch (error) {
			console.error("Error loading vacation data:", error);
		}
	};

	loadHourData = async (employee = null) => {
		try {
			// Usar el employee pasado como parámetro o el del estado
			const employeeToUse = employee || this.state.employee;

			// Validación defensiva: verificar que employee y employee.id existan
			if (!employeeToUse || !employeeToUse.id) {
				console.warn(
					"No se puede cargar hour data: employee o employee.id es undefined",
					{
						employeeParam: employee,
						stateEmployee: this.state.employee,
					}
				);
				return;
			}

			const response = await dashboardsService.getEmployeeHourSummary(
				employeeToUse.id
			);

			if (response.ok) {
				this.setState({ hourData: response });
			}
		} catch (error) {
			console.error("Error loading hour data:", error);
		}
	};

	loadSidebarData = async (employee = null) => {
		// Usar el employee pasado como parámetro o el del estado
		const employeeToUse = employee || this.state.employee;

		// Validación defensiva: verificar que employee y employee.id existan
		if (!employeeToUse || !employeeToUse.id) {
			console.warn(
				"No se puede cargar sidebar data: employee o employee.id es undefined",
				{
					employeeParam: employee,
					stateEmployee: this.state.employee,
				}
			);
			return;
		}

		// Cargar datos necesarios para el sidebar en paralelo
		// Pasar employee directamente para evitar problemas de timing con setState asíncrono
		await Promise.all([
			this.loadVacationData(employeeToUse),
			this.loadHourData(employeeToUse),
			this.loadTimeRegistrations(employeeToUse),
			this.props.getWidgetTemplates(), // Cargar plantillas de widgets (pestaña General / EmployeeInfo)
		]);

		// Cargar datos de la tab inicial (info) - solo una vez
		if (!this.state.loadedTabs.has("info")) {
			await this.loadTabData("info");
		}
	};

	handleEmployeeSelectorChange = (idStr, emp) => {
		if (!idStr) return;
		const selectedId = Number(idStr);
		if (Number.isNaN(selectedId)) return;

		// Si ya estamos viendo ese empleado no hacemos navegación.
		if (selectedId === this.state.employee.id) {
			return;
		}

		this.setState({
			loading: true,
			employee: {},
		});

		this.props.clearEmployeeState();
		this.props.history.push(`/employees/${selectedId}`);
	};

	loadTimeRegistrations = async (employee = null) => {
		try {
			// Usar el employee pasado como parámetro o el del estado
			const employeeToUse = employee || this.state.employee;

			// Validación defensiva: verificar que employee y employee.id existan
			if (!employeeToUse || !employeeToUse.id) {
				console.warn(
					"No se puede cargar time registrations: employee o employee.id es undefined",
					{
						employeeParam: employee,
						stateEmployee: this.state.employee,
					}
				);
				return;
			}

			const today = moment().format("YYYY-MM-DD");
			const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");

			// Buscar fichajes del día actual
			const todayResponse = await timesService.getAll({
				employee_id: employeeToUse.id,
				start_from: today,
				start_to: today,
				page: 0,
			});

			// Buscar fichajes en curso del día anterior (para turnos nocturnos)
			const yesterdayResponse = await timesService.getAll({
				employee_id: employeeToUse.id,
				start_from: yesterday,
				start_to: yesterday,
				page: 0,
			});

			let timeRegistrations = [];
			let ongoingTime = null;
			let firstEntryTime = null;
			let todayTotalTime = null;

			// Procesar fichajes del día actual
			if (
				todayResponse.ok &&
				todayResponse.data &&
				todayResponse.data.length > 0
			) {
				timeRegistrations = todayResponse.data;
				ongoingTime = timeRegistrations.find((time) => !time.end);
				firstEntryTime = timeRegistrations[0];
				todayTotalTime = ongoingTime
					? null
					: getWorkedTimeCounter(timeRegistrations);
			}

			// Buscar fichajes del día anterior (para turnos nocturnos)
			if (
				yesterdayResponse.ok &&
				yesterdayResponse.data &&
				yesterdayResponse.data.length > 0
			) {
				const yesterdayRegistrations = yesterdayResponse.data;

				// Filtrar fichajes que empezaron ayer pero terminaron hoy o están en curso
				const relevantYesterdayRegistrations = yesterdayRegistrations.filter(
					(time) => {
						// Si no tiene hora de fin, está en curso (turno nocturno)
						if (!time.end) return true;

						// Si tiene hora de fin, verificar si terminó hoy
						const endDate = moment(time.end).format("YYYY-MM-DD");
						return endDate === today;
					}
				);

				if (relevantYesterdayRegistrations.length > 0) {
					// Combinar los registros relevantes del día anterior con los del día actual
					timeRegistrations = [
						...relevantYesterdayRegistrations,
						...timeRegistrations,
					];

					// Buscar si hay un fichaje en curso (del día anterior o actual)
					const foundOngoingTime = timeRegistrations.find((time) => !time.end);
					if (foundOngoingTime) {
						ongoingTime = foundOngoingTime;
					}

					// El firstEntryTime debe ser el más antiguo de todos los registros relevantes
					if (timeRegistrations.length > 0) {
						const sortedRegistrations = timeRegistrations.sort((a, b) =>
							moment(a.start).isBefore(moment(b.start)) ? -1 : 1
						);
						firstEntryTime = sortedRegistrations[0];
					}

					// Calcular el tiempo total de todos los registros completados
					const completedRegistrations = timeRegistrations.filter(
						(time) => time.end
					);
					if (completedRegistrations.length > 0) {
						todayTotalTime = getWorkedTimeCounter(completedRegistrations);
					}
				}
			}

			this.setState({
				timeRegistrations,
				ongoingTime,
				todayTotalTime,
				firstEntryTime,
				hasTimeRegistrations:
					timeRegistrations.length > 0 || ongoingTime !== null,
			});
		} catch (error) {
			console.error("Error loading time registrations:", error);
			this.setState({
				timeRegistrations: [],
				ongoingTime: null,
				todayTotalTime: null,
				firstEntryTime: null,
				hasTimeRegistrations: false,
			});
		}
	};

	// Funciones para cargar datos de tabs bajo demanda
	loadTabData = async (tabKey) => {
		const loadingPromises = [];

		switch (tabKey) {
			case "grupos":
				loadingPromises.push(this.props.getAllGroups());
				break;
			case "campos-personalizados":
				loadingPromises.push(this.props.getAllCustomFields());
				break;
			case "funciones":
				loadingPromises.push(this.props.getEnabledFunctions());
				break;
			case "ruedas-turnos":
				loadingPromises.push(this.props.getSelectableShiftPatterns());
				break;
			case "ubicaciones":
				loadingPromises.push(this.props.getEnabledUbications());
				break;
			case "contratos":
				loadingPromises.push(this.props.getContracts());
				break;
			case "documentos":
				// Los documentos se cargan automáticamente en el componente cuando se monta
				// Pero también refrescamos el estado para forzar la recarga
				this.setState((prevState) => ({
					documentosRefreshKey: (prevState.documentosRefreshKey || 0) + 1,
				}));
				break;
			default:
				break;
		}

		if (loadingPromises.length > 0) {
			await Promise.all(loadingPromises);
		}

		// Marcar la tab como cargada (pero siempre refrescar al cambiar)
		this.setState((prevState) => ({
			loadedTabs: new Set([...prevState.loadedTabs, tabKey]),
		}));
	};

	handleTabSelect = async (tabKey) => {
		this.setState({ activeTab: tabKey });
		// Siempre cargar/refrescar datos al cambiar de pestaña
		await this.loadTabData(tabKey);
	};

	handleConfigurationChanged = () => {
		this.setState((prevState) => ({
			configurationWarningsRefreshKey:
				prevState.configurationWarningsRefreshKey + 1,
		}));
	};

	handleConfigurationWarningAction = async (tabKey) => {
		await this.handleTabSelect(tabKey);
		this.setState({ assignmentModalTab: tabKey });
	};

	handleAssignmentRequestHandled = () => {
		this.setState({ assignmentModalTab: null });
	};

	formatHours = (hours) => {
		if (!hours || hours === 0) return "0";
		return hours.toLocaleString(undefined, { maximumFractionDigits: 2 });
	};

	calculateProgressPercentage = (worked, target) => {
		if (!target || target === 0) return 0;
		return Math.min((worked / target) * 100, 100);
	};

	handleBackClick = () => {
		// Limpiar el estado global antes de salir
		this.props.clearEmployeeState();
		this.props.history.goBack();
	};

	handleEmployeeUpdated = (updatedEmployee) => {
		// Validación defensiva: verificar que updatedEmployee y updatedEmployee.id existan
		if (!updatedEmployee || !updatedEmployee.id) {
			console.warn(
				"No se puede actualizar empleado: updatedEmployee o updatedEmployee.id es undefined"
			);
			return;
		}

		// Actualizar el estado local del empleado cuando se actualiza desde EmployeeInfo
		this.setState({
			employee: updatedEmployee,
		});

		// También actualizar el estado global del reducer
		this.props.updateEmployeeInState(updatedEmployee);

		// Recargar los datos del sidebar para reflejar los cambios
		// Pasar updatedEmployee directamente para evitar problemas de timing con setState asíncrono
		this.loadSidebarData(updatedEmployee);
	};

	formatSeniority = (seniority) => {
		const t = this.props.t;
		if (!seniority) return t("employee.noSeniority");

		const { years, months, days } = seniority;
		const parts = [];

		if (years > 0) {
			parts.push(`${years} ${years === 1 ? t("employees.seniorityYear") : t("employees.seniorityYears")}`);
		}

		if (months > 0) {
			parts.push(`${months} ${months === 1 ? t("employees.seniorityMonth") : t("employees.seniorityMonths")}`);
		}

		if (days > 0) {
			parts.push(`${days} ${days === 1 ? t("employees.seniorityDay") : t("employees.seniorityDays")}`);
		}

		if (parts.length === 0) {
			return t("employee.noSeniority");
		}

		if (parts.length === 1) {
			return parts[0];
		}

		const and = t("employee.seniorityAnd");
		if (parts.length === 2) {
			return `${parts[0]} ${and} ${parts[1]}`;
		}

		return `${parts[0]}, ${parts[1]} ${and} ${parts[2]}`;
	};

	getEmployeeCard = () => {
		// Usar datos del estado global del reducer si están disponibles y son del empleado actual, sino usar el estado local
		const employeeFromReducer = this.props.employeeFromStore;
		const employeeFromState = this.state.employee;
		const employee =
			employeeFromReducer &&
			employeeFromReducer.id &&
			employeeFromReducer.id === this.state.employee.id
				? employeeFromReducer
				: employeeFromState;

		const { user } = this.props;
		const groups_supervised = user.groups_supervised;
		const isSupervisorWithEditDocuments =
			isSupervisor() &&
			groups_supervised.length > 0 &&
			groups_supervised.some((gs) => gs.edit_documents);
		const showAdminDocuments = isAdmin() || isSupervisorWithEditDocuments;

		return (
			<div className="employee-detail-container">
				{/* Left Column - Employee Summary */}
				<div className="employee-sidebar">
					{/* Profile Section */}
					<div className="profile-section">
						<div className="avatar-container">
							<Avatar
								onClick={this.openAvatarModal}
								userId={employee.id}
								className="avatar-large"
								alt={employee.name}
							/>
							<button
								className="avatar-edit-btn"
								onClick={this.openAvatarModal}
							>
								<FiEdit3 />
							</button>
						</div>

						<div className="employee-data">
							<h2 className="employee-data-name">
								{employee.name} {employee.surname}
							</h2>

							<p className="employee-data-position">
								{employee.position || this.props.t("employee.noPosition")}
							</p>
							<p className="employee-data-seniority">
								{this.formatSeniority(employee.seniority)}
							</p>

							<div className="employee-data-contact-info">
								{employee.email && (
									<a
										href={`mailto:${employee.email}`}
										className="employee-data-contact-item"
									>
										<FiMail />
										{employee.email}
									</a>
								)}
								{employee.phone && (
									<a
										href={`tel:${employee.phone}`}
										className="employee-data-contact-item"
									>
										<FiPhone />
										{employee.phone}
									</a>
								)}
							</div>
						</div>
					</div>

					{/* Action Buttons Section */}
					<div className="employee-action-buttons">
						<button
							className="action-btn action-btn-plan"
							onClick={this.openPlanModal}
						>{this.props.t('employee.planBtn')}
						</button>
						<button
							className="action-btn action-btn-ausencias"
							onClick={() =>
								window.open(`/absences?employee=${employee.id}`, "_blank")
							}
						>{this.props.t('employee.absencesBtn')}
						</button>
						{(() => {
							const hasMyCalendarSubscription =
								hasSubscriptionFeature("MY_CALENDAR");

							return (
								<button
									className={`action-btn action-btn-calendario ${!hasMyCalendarSubscription ? "disabled-subscription" : ""}`}
									onClick={(e) => {
										e.preventDefault();
										if (hasMyCalendarSubscription) {
											window.open(
												`/calendar?employee=${employee.id}`,
												"_blank"
											);
										} else {
											this.setState({
												showUpgradeModal: true,
												upgradeFeature: this.props.t("employee.calendarBtn"),
											});
										}
									}}
									style={{
										opacity: !hasMyCalendarSubscription ? 0.5 : 1,
										cursor: !hasMyCalendarSubscription
											? "not-allowed"
											: "pointer",
										width: "100%",
									}}
								>{this.props.t('employee.calendarBtn')}
								</button>
							);
						})()}
						<button
							className="action-btn action-btn-fichajes"
							onClick={() =>
								this.props.history.push(`/times/${employee.id}/employee`)
							}
						>{this.props.t('employee.timesBtn')}
						</button>
					</div>

					{/* Clock-in Status */}
					{this.state.hasTimeRegistrations && (
						<div className="clock-in-section">
							<div className="clock-in-header">
								<h3>
									{this.state.ongoingTime
										? this.props.t("employee.clockInOngoing")
										: this.props.t("employee.clockInDay")}
								</h3>
							</div>
							<div className="clock-in-content">
								{this.state.ongoingTime ? (
									<EmployeeTimer startTime={this.state.ongoingTime.start} />
								) : (
									<div className="timer">
										<div className="timer-item">
											<div className="timer-value">
												{this.state.todayTotalTime?.split(":")[0] || "00"}
											</div>
											<div className="timer-key">h</div>
										</div>
										<div className="timer-item">
											<div className="timer-value">
												{this.state.todayTotalTime?.split(":")[1] || "00"}
											</div>
											<div className="timer-key">m</div>
										</div>
										<div className="timer-item">
											<div className="timer-value">
												{this.state.todayTotalTime?.split(":")[2] || "00"}
											</div>
											<div className="timer-key">s</div>
										</div>
									</div>
								)}
								<div className="clock-in-details">
									<p className="status">
										{this.state.ongoingTime
											? this.props.t("employee.clockInStatus", { type: this.state.ongoingTime.type?.name || this.props.t("employee.clockInWork") })
											: this.props.t("employee.clockInTotal")}
									</p>
									<hr />
									<p className="entry-time">
										<span>{this.props.t("employee.clockInEntry")}</span>
										<strong className="entry-time-value">
											{this.state.firstEntryTime
												? moment
														.utc(this.state.firstEntryTime.start)
														.local()
														.format("HH:mm")
												: "--:--"}
										</strong>
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Hours Worked */}
					<div className="hours-section">
						<h3>{this.props.t("employee.hoursWorked")}</h3>
						<div className="hours-progress">
							<div className="progress-bar">
								<div
									className="progress-fill"
									style={{
										width: `${this.calculateProgressPercentage(
											this.state.hourData?.current_week
												?.time_registration_hours || 0,
											this.state.hourData?.current_week?.target_hours || 0
										)}%`,
									}}
								></div>
							</div>
							<div className="hours-info">
								<div className="worked-hours">
									<div className="worked-hours-item">
										<div className="worked-hours-value">
											{this.formatHours(
												this.state.hourData?.current_week
													?.time_registration_hours || 0
											)}
										</div>
										<div className="worked-hours-key">h</div>
									</div>
								</div>
								<div className="target-hours">
									<div className="worked-hours-item">
										<div className="worked-hours-value">
											{this.formatHours(
												this.state.hourData?.current_week?.target_hours || 0
											)}
										</div>
										<div className="worked-hours-key">h</div>
									</div>
								</div>
							</div>
							<div className="hours-legend">
								<span className="legend-item">
									<span className="legend-color worked"></span>
									{this.props.t('employee.legendWorked')}
								</span>
								<span className="legend-item">
									<span className="legend-color remaining"></span>{this.props.t('employee.legendRemaining')}
								</span>
							</div>
						</div>
					</div>

					{/* Holidays */}
					<div className="holidays-section">
						<div className="holidays-item">
							<div className="holidays-key">{this.props.t("employee.vacationGenerated")}</div>
							<div className="holidays-value">
								{this.state.vacationData?.vacation_entitlement?.toLocaleString(
									undefined,
									{ maximumFractionDigits: 2 }
								) || 0}
							</div>
						</div>
						<div className="holidays-item">
							<div className="holidays-key">{this.props.t("employee.vacationTaken")}</div>
							<div className="holidays-value">
								{this.state.vacationData?.vacation_taken_already_done?.toLocaleString(
									undefined,
									{ maximumFractionDigits: 2 }
								) || 0}
							</div>
						</div>
						<div className="holidays-item">
							<div className="holidays-key">{this.props.t("employee.vacationPlanned")}</div>
							<div className="holidays-value">
								{this.state.vacationData?.vacation_taken_not_done?.toLocaleString(
									undefined,
									{ maximumFractionDigits: 2 }
								) || 0}
							</div>
						</div>
						<div className="holidays-item">
							<div className="holidays-key">{this.props.t("employee.vacationAvailable")}</div>
							<div className="holidays-value">
								{this.state.vacationData?.vacation_pending?.toLocaleString(
									undefined,
									{ maximumFractionDigits: 2 }
								) || 0}
							</div>
						</div>
					</div>
				</div>

				{/* Right Column - Detailed Information */}
				<div className="employee-main-content">
					<Tab.Container
						defaultActiveKey="info"
						activeKey={this.state.activeTab}
						onSelect={this.handleTabSelect}
					>
						<Nav variant="tabs" className="employee-tabs">
							<Nav.Item>
								<Nav.Link eventKey="info">{this.props.t('employee.tabGeneral')}</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="contratos">{this.props.t('employee.tabContracts')}</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="grupos">{this.props.t('employee.tabGroups')}</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="ruedas-turnos">{this.props.t('employee.tabShiftPatterns')}</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="funciones">{this.props.t('employee.tabFunctions')}</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="ubicaciones">{this.props.t('employee.tabUbications')}</Nav.Link>
							</Nav.Item>
							{showAdminDocuments && (
								<Nav.Item>
									<Nav.Link eventKey="documentos">{this.props.t('employee.tabDocuments')}</Nav.Link>
								</Nav.Item>
							)}
							<Nav.Item>
								<Nav.Link eventKey="campos-personalizados">
									{this.props.t('employee.tabCustomFields')}
								</Nav.Link>
							</Nav.Item>
							{isAdmin() && (
								<Nav.Item>
									<Nav.Link eventKey="nomina">
										{this.props.t('employee.tabPayroll')}
									</Nav.Link>
								</Nav.Item>
							)}
							<Nav.Item>
								<Nav.Link eventKey="planificacion">
									{this.props.t('employee.tabMagicPlan')} <FaMagic className="icon-wand" />
								</Nav.Link>
							</Nav.Item>
						</Nav>
						<Tab.Content className="employee-tab-content">
							{/* mountOnEnter + unmountOnExit: solo la pestaña activa monta hijos y sus peticiones;
							    sin esto, todos los Tab.Pane montan a la vez y cada componentDidMount dispara APIs */}
							<Tab.Pane eventKey="info" mountOnEnter unmountOnExit>
								<EmployeeInfo
									employee={employee}
									onEmployeeUpdated={this.handleEmployeeUpdated}
								/>
							</Tab.Pane>
							<Tab.Pane eventKey="grupos" mountOnEnter unmountOnExit>
								<EmployeeGroups
									employee={employee}
									onDataChanged={this.handleConfigurationChanged}
									openAssignment={this.state.assignmentModalTab === "grupos"}
									onAssignmentRequestHandled={this.handleAssignmentRequestHandled}
								/>
							</Tab.Pane>
							<Tab.Pane eventKey="campos-personalizados" mountOnEnter unmountOnExit>
								<EmployeeCustomFields employee={employee} />
							</Tab.Pane>
							<Tab.Pane eventKey="funciones" mountOnEnter unmountOnExit>
								<EmployeeFunctions employee={employee} />
							</Tab.Pane>
							<Tab.Pane eventKey="ubicaciones" mountOnEnter unmountOnExit>
								<EmployeeUbications employee={employee} />
							</Tab.Pane>
							<Tab.Pane eventKey="ruedas-turnos" mountOnEnter unmountOnExit>
								<EmployeeShiftPatterns employee={employee} />
							</Tab.Pane>
							<Tab.Pane eventKey="contratos" mountOnEnter unmountOnExit>
								<EmployeeContracts
									employee={employee}
									onDataChanged={this.handleConfigurationChanged}
									openAssignment={this.state.assignmentModalTab === "contratos"}
									onAssignmentRequestHandled={this.handleAssignmentRequestHandled}
								/>
							</Tab.Pane>
							{isAdmin() && (
								<Tab.Pane eventKey="nomina" mountOnEnter unmountOnExit>
									<EmployeePayroll
										employee={employee}
										onEmployeeUpdated={this.handleEmployeeUpdated}
									/>
								</Tab.Pane>
							)}
							{showAdminDocuments && (
								<Tab.Pane eventKey="documentos" mountOnEnter unmountOnExit>
									<EmployeeDocuments
										employee={employee}
										isVisible={this.state.activeTab === "documentos"}
										refreshKey={this.state.documentosRefreshKey}
									/>
								</Tab.Pane>
							)}
							<Tab.Pane eventKey="planificacion" mountOnEnter unmountOnExit>
								<EmployeeMagicPlan
									employee={employee}
									onEmployeeUpdated={this.handleEmployeeUpdated}
								/>
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</div>
			</div>
		);
	};

	getContent = () => {
		if (this.state.loading) {
			return <Loading />;
		}

		if (!this.state.employee.id) {
			return this.props.t("employee.loadError");
		}
		return (
			<>
				{/* <AbsenceHeader employee_id={this.state.employee.id} /> */}
				<EmployeePendingConfigurationWarnings
					employeeId={this.state.employee.id}
					onNavigate={this.handleConfigurationWarningAction}
					refreshKey={this.state.configurationWarningsRefreshKey}
					className="mb-3"
				/>
				{this.getEmployeeCard()}
			</>
		);
	};

	render() {
		return (
			<>
				<Layout className="page-employee">
					<div className="heading">
						<div className="heading-left">
							<h1 className="title">
								<FiArrowLeft
									className="button-icon"
									onClick={this.handleBackClick}
								/>{" "}
								{this.props.t('employees.title')}
							</h1>
							<div className="employee-selector">
								<EmployeeSelectSearch
									className="form-select-user mb-0"
									controlId="employee-selector"
									multiple={false}
									allowClear={false}
									value={
										this.state.employee?.id
											? String(this.state.employee.id)
											: null
									}
									onChange={this.handleEmployeeSelectorChange}
									fetchOptions={{ enabled: true }}
									placeholder={this.props.t("employee.searchPlaceholder")}
									knownEmployee={this.state.employee}
								/>
							</div>
						</div>
					</div>

					{this.getContent()}
				</Layout>
				<Modal hideModal={this.props.hideModal} />

				{/* Modal de upgrade para funcionalidades bloqueadas */}
				<UpgradeModal
					show={this.state.showUpgradeModal}
					onClose={() => this.setState({ showUpgradeModal: false })}
					feature={this.state.upgradeFeature}
					requiredPlan="Pro"
				/>
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	const employeesState = reducers.employeesReducer || {};
	const { employee: employeeFromStore, ...employeesRest } = employeesState;

	return {
		...employeesRest,
		employeeFromStore,
		...reducers.groupsReducer,
		...reducers.functionsReducer,
		...reducers.ubicationsReducer,
		...reducers.shiftsReducer,
		...reducers.rulesReducer,
		...reducers.customFieldsReducer,
		...reducers.dashboardReducer,
		user: reducers.authReducer.user,
		loading: reducers.employeesReducer.loading,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	getEmployee: (employee_id) =>
		dispatch(employeesActions.getEmployee(employee_id)),
	getAllCustomFields: () => dispatch(customFieldsActions.getAllCustomFields()),
	getAllGroups: () =>
		dispatch(groupsActions.getAllGroups({ is_multigroup: false })),
	getEnabledFunctions: () => dispatch(functionsActions.getEnabledFunctions()),
	getEnabledUbications: () =>
		dispatch(ubicationsActions.getEnabledUbications()),
	getContracts: () => dispatch(rulesActions.getContracts()),
	getSelectableShiftPatterns: () =>
		dispatch(shiftsActions.getSelectableShiftPatterns()),
	getWidgetTemplates: () => dispatch(dashboardActions.getWidgetTemplates()),
	updateEmployeeInState: (employee) =>
		dispatch(employeesActions.updateEmployeeInState(employee)),
	clearEmployeeState: () => dispatch(employeesActions.clearEmployeeState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Employee));
